/**
 * Award Constants
 * @package @comp-sys/shared
 */
import { AwardType, PresetAwardType } from '../types/awards.js';
/**
 * Award type labels with i18n support
 */
export const AWARD_TYPE_LABELS = {
    [AwardType.CHAMPION]: {
        'zh-CN': '冠军',
        'en-US': 'Champion',
    },
    [AwardType.RUNNER_UP]: {
        'zh-CN': '亚军',
        'en-US': 'Runner Up',
    },
    [AwardType.THIRD]: {
        'zh-CN': '季军',
        'en-US': 'Third Place',
    },
    [AwardType.GOLD]: {
        'zh-CN': '金奖',
        'en-US': 'Gold',
    },
    [AwardType.SILVER]: {
        'zh-CN': '银奖',
        'en-US': 'Silver',
    },
    [AwardType.BRONZE]: {
        'zh-CN': '铜奖',
        'en-US': 'Bronze',
    },
    [AwardType.EXCELLENCE]: {
        'zh-CN': '优秀奖',
        'en-US': 'Excellence',
    },
    [AwardType.PARTICIPATION]: {
        'zh-CN': '参与奖',
        'en-US': 'Participation',
    },
    [AwardType.SPECIAL]: {
        'zh-CN': '特别奖',
        'en-US': 'Special Award',
    },
};
/**
 * Get award type label for a specific locale
 */
export function getAwardTypeLabel(type, locale = 'en-US') {
    return AWARD_TYPE_LABELS[type]?.[locale] ?? AWARD_TYPE_LABELS[type]?.['en-US'] ?? type;
}
/**
 * Award type badge colors (Tailwind classes)
 */
export const AWARD_TYPE_COLORS = {
    [AwardType.CHAMPION]: 'bg-yellow-500 text-yellow-950',
    [AwardType.RUNNER_UP]: 'bg-gray-300 text-gray-800',
    [AwardType.THIRD]: 'bg-orange-400 text-orange-950',
    [AwardType.GOLD]: 'bg-yellow-400 text-yellow-950',
    [AwardType.SILVER]: 'bg-gray-300 text-gray-800',
    [AwardType.BRONZE]: 'bg-orange-300 text-orange-950',
    [AwardType.EXCELLENCE]: 'bg-blue-400 text-blue-950',
    [AwardType.PARTICIPATION]: 'bg-green-400 text-green-950',
    [AwardType.SPECIAL]: 'bg-purple-400 text-purple-950',
};
/**
 * Available icons for custom awards (lucide-react)
 */
export const AWARD_ICON_OPTIONS = [
    'trophy',
    'medal',
    'award',
    'star',
    'gem',
    'crown',
    'target',
    'flag',
    'sparkles',
    'flame',
    'zap',
    'heart',
    'thumbs-up',
];
/**
 * Default color palette for custom awards
 */
export const AWARD_COLOR_OPTIONS = [
    '#ef4444', // red-500
    '#f97316', // orange-500
    '#eab308', // yellow-500
    '#22c55e', // green-500
    '#06b6d4', // cyan-500
    '#3b82f6', // blue-500
    '#8b5cf6', // violet-500
    '#ec4899', // pink-500
];
/**
 * Preset award type labels with i18n support
 */
export const PRESET_AWARD_TYPE_LABELS = {
    [PresetAwardType.CHAMPION]: {
        'zh-CN': '冠军',
        'en-US': 'Champion',
    },
    [PresetAwardType.RUNNER_UP]: {
        'zh-CN': '亚军',
        'en-US': 'Runner Up',
    },
    [PresetAwardType.THIRD]: {
        'zh-CN': '季军',
        'en-US': 'Third Place',
    },
    [PresetAwardType.FIRST_PRIZE]: {
        'zh-CN': '一等奖',
        'en-US': 'First Prize',
    },
    [PresetAwardType.SECOND_PRIZE]: {
        'zh-CN': '二等奖',
        'en-US': 'Second Prize',
    },
    [PresetAwardType.THIRD_PRIZE]: {
        'zh-CN': '三等奖',
        'en-US': 'Third Prize',
    },
    [PresetAwardType.BEST_DESIGN]: {
        'zh-CN': '最佳设计奖',
        'en-US': 'Best Design Award',
    },
};
/**
 * Get preset award type label for a specific locale
 */
export function getPresetAwardTypeLabel(type, locale = 'en-US') {
    return PRESET_AWARD_TYPE_LABELS[type]?.[locale] ?? PRESET_AWARD_TYPE_LABELS[type]?.['en-US'] ?? type;
}
/**
 * Preset award type badge colors (Tailwind classes)
 */
export const PRESET_AWARD_TYPE_COLORS = {
    [PresetAwardType.CHAMPION]: 'bg-yellow-500 text-yellow-950',
    [PresetAwardType.RUNNER_UP]: 'bg-gray-300 text-gray-800',
    [PresetAwardType.THIRD]: 'bg-orange-400 text-orange-950',
    [PresetAwardType.FIRST_PRIZE]: 'bg-yellow-400 text-yellow-950',
    [PresetAwardType.SECOND_PRIZE]: 'bg-gray-300 text-gray-800',
    [PresetAwardType.THIRD_PRIZE]: 'bg-orange-300 text-orange-950',
    [PresetAwardType.BEST_DESIGN]: 'bg-purple-400 text-purple-950',
};
/**
 * Resolve a human-readable award display name from whatever identifying
 * fields are available. Handles, in order:
 *   1. Preset awards — matched via `presetType` (preferred) or `type`, looked
 *      up in PRESET_AWARD_TYPE_LABELS. This is what fixes awards whose stored
 *      title is a raw code like "FIRST_PRIZE" (auto-assign historically fell
 *      back to the legacy AwardType map, which has no FIRST_PRIZE entry).
 *   2. Legacy AwardType (CHAMPION/GOLD/...) via AWARD_TYPE_LABELS.
 *   3. Custom awards — via the multilingual `customName` map (locale, then
 *      zh-CN fallback).
 *   4. The provided `fallback` (e.g. the stored title) when nothing matches.
 *
 * Award names are competition-defined domain content, not UI chrome, so the
 * locale defaults to the system locale zh-CN.
 */
export function resolveAwardDisplayName(opts, locale = 'zh-CN') {
    const presetValues = Object.values(PresetAwardType);
    const legacyValues = Object.values(AwardType);
    const preset = opts.presetType ?? opts.type;
    if (preset && presetValues.includes(preset)) {
        return getPresetAwardTypeLabel(preset, locale);
    }
    if (opts.type && legacyValues.includes(opts.type)) {
        return getAwardTypeLabel(opts.type, locale);
    }
    if (opts.customName) {
        return opts.customName[locale] ?? opts.customName['zh-CN'] ?? opts.fallback ?? '';
    }
    return opts.fallback ?? '';
}
//# sourceMappingURL=awards.js.map